import type { RouteLocationRaw } from 'vue-router';

import { ComputeProviderName, DeploymentState } from '@clovyr/pollen';
import { WNextAccessAPI } from '@clovyr/pollen/deployment/access';
import { getHostByID } from '@clovyr/pollen/fixtures/hosts';
import { wnextHTTP } from '@clovyr/pollen/http';
import {
  addBackupValues,
  CLOVYR_ACCESS_KEY,
  CLOVYR_BACKUP_PASSWORD,
  CLOVYR_WNEXT_FQDN,
  CLOVYR_S3_URL,
  CLOVYR_SECRET_KEY,
  dumpYaml,
  type Manifest,
  type Values,
} from '@clovyr/pollen/manifest';
import { getBuilderForManifest } from '@clovyr/pollen/manifest/configure';
import { writeAllFilesBase64, writeBase64File } from '@clovyr/pollen/manifest/configure/util';
import {
  addScheme,
  claimInstance,
  type ClaimInstanceResticConfig,
  execWithOutput,
  waitForInstance,
} from '@clovyr/pollen/remote_exec';
import { getDeployments } from '@clovyr/pollen/remote_exec/kubernetes';
import type { Deployment } from '@clovyr/pollen/types';

import { config } from '@/composables/useInstanceLauncher';
import { config as appConfig } from '@/init/config';
import { useDeploymentStore } from '@/stores/deployment_store';
import { usePollenStore } from '@/stores/pollen_store';

import entrypoint from '../../../../go/trefoil/configure/entrypoint.sh?raw';

function redirectToDep(dep: Deployment, code?: boolean) {
  // send to framed view or redirect to the app itself
  return {
    name: code ? 'ViewDeployment' : 'OpenDeployment',
    params: { deploymentName: dep.instanceName },
  };
}

function resticConfigFromValues(values: Values): ClaimInstanceResticConfig {
  const getValue = (key: string): string => {
    const value = values[key];
    if (typeof value !== 'string') {
      throw new Error(`expected string for key ${key}, got ${typeof value}`);
    }
    return value;
  };

  return {
    awsAccessKeyID: getValue(CLOVYR_ACCESS_KEY),
    awsSecretAccessKey: getValue(CLOVYR_SECRET_KEY),
    password: getValue(CLOVYR_BACKUP_PASSWORD),
    repository: getValue(CLOVYR_S3_URL),
  };
}

const GENERIC_APP_ID = 'code-generic-esgz';
const DOCKER_PROXY_URL = 'http://10.108.0.92:3128/';
// const DOCKER_REGISTRY = ''; // ghcr.io/

const CODE_IMAGES = {
  'code-bitcoin': 'clovyr/code-bitcoin:dev-esgz',
  'code-elm': 'clovyr/code-elm:dev-esgz',
  'code-go': 'clovyr/code-go:dev-esgz',
  'code-haskell': 'clovyr/code-haskell:dev-esgz',
  'code-javascript': 'clovyr/code-javascript:dev-esgz',
  'code-python': 'clovyr/code-python:dev-esgz',
  'code-ruby': 'clovyr/code-ruby:dev-esgz',
  'code-rust': 'clovyr/code-rust:dev-esgz',
  'code-ubuntu': 'clovyr/code-ubuntu:jammy-esgz',
};

async function runCodeImageCompose(
  appID: string,
  host: string,
  configureKey: string,
  app: Manifest,
  values: Values,
  upCmd = 'docker compose up', // --no-deps // nerdctl compose --snapshotter soci up
) {
  console.log('going to deploy code image', appID, host);
  const image = CODE_IMAGES[appID];

  values.CODE_IMAGE = image;
  const [, , files] = getBuilderForManifest(app).prepareConfigureData(app, values);

  const script = `#!/bin/bash
set -xeo pipefail

mkdir -p /opt/clovyr/apps/clovyr-code
cd /opt/clovyr/apps/clovyr-code

${writeAllFilesBase64(files)}

export HTTP_PROXY=${DOCKER_PROXY_URL}
export HTTPS_PROXY=${DOCKER_PROXY_URL}

docker compose kill clovyr-restic-backup code # TODO: extract var?
${upCmd} -d code
`;

  const res = await execWithOutput(host, configureKey, script);
  console.timeLog('runCode', 'applied update, waiting for ready');
  if (!res.ok) {
    throw new Error('failed to update deployment');
  }
  console.log(await res.text());

  const status = await waitForInstance(addScheme(host), configureKey, 500, 200, 500);
  if (!status) {
    throw new Error('instance did not come up');
  }
}

/**
 * Re-deploy the deployment with the requested code image.
 *
 * This is an optimization for booting, e.g., code-go, quicker as we only have to pull the diff
 * between code-ubuntu and the target image.
 *
 * @param appID
 * @param host
 * @param configureKey
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function runCodeImageKube(appID: string, host: string, configureKey: string) {
  console.log('going to redeploy code image', appID, host);
  const deployments = await getDeployments(host, configureKey);
  console.timeLog('runCode', 'fetched deployments');
  if (!deployments || deployments.length === 0) {
    throw new Error('no deployments found');
  }

  // should only be two deployments, usually the first one but filter out restic
  const deployment = deployments.find((d) => d.metadata?.name !== 'clovyr-restic');
  const container = deployment?.spec?.template.spec?.containers?.[0];
  if (!container) {
    throw new Error('no containers found');
  }
  console.log('got deployment', deployment);
  container.image = CODE_IMAGES[appID];
  container.args = ['-c', entrypoint];

  const script = `#!/bin/bash
set -eo pipefail

${writeBase64File('/tmp/update-deployment.yaml', dumpYaml(deployment))}
kubectl apply -f /tmp/update-deployment.yaml
  `;

  const res = await execWithOutput(host, configureKey, script);
  console.timeLog('runCode', 'applied update, waiting for ready');
  if (!res.ok) {
    throw new Error('failed to update deployment');
  }

  // wait for ready
  // waits for pod to come up
  // const checkPath = '/_leaf/ready?type=clovyr-code';
  // let status = await waitForInstance(addScheme(host) + checkPath, configureKey, 1000, 200);
  // if (!status) {
  //   throw new Error('instance did not come up');
  // }
  // wait for / to return 200
  const status = await waitForInstance(addScheme(host), configureKey, 500, 200, 500);
  if (!status) {
    throw new Error('instance did not come up');
  }
}

/**
 * Claim a clovyr code instance or show existing instance if they already have one
 *
 * @param appID should be '[publisher]/id', e.g., 'foobar/code-my-develop-app' or 'clovyr-code'
 * @param deploymentName instance to claim from backend (e.g., kanboard, code-go)
 * @param fqdn fqdn to search for existing deployment
 * @param isCode true if this is a clovyr code instance (false for custom apps)
 * @returns
 */
export async function claimClovyrCode(
  appID: string,
  deploymentName: string,
  fqdn?: string,
  isCode?: boolean,
  token?: string,
): Promise<RouteLocationRaw> {
  console.log('claim clovyr code', appID, deploymentName, fqdn, isCode, token);
  const deploymentStore = useDeploymentStore();
  const pollenStore = usePollenStore();
  const { garden } = pollenStore;

  const useStarGz = isCode && !!CODE_IMAGES[appID];

  let app: Manifest | undefined;
  app = garden.getAppByID(useStarGz ? GENERIC_APP_ID : appID);
  if (!app && isCode) {
    // fallback. not sure we need this, here for backwards compat
    // since we no longer pass 'clovyr-code' as appID when claiming from instant page.
    app = garden.getAppByID('clovyr-code');
  }
  if (!app) {
    throw new Error('app not found');
  }
  const loading = garden.loadDeploymentDetails(app);

  let dep: Deployment | undefined;
  if (fqdn) {
    // got fqdn via some other method, use it to look up existing deployment
    // if we have it, open it immediately or send to library, as needed.
    dep = pollenStore.deployments.find((d) => d.fqdn === fqdn);
    if (dep) {
      return redirectToDep(dep, isCode);
    }
  }

  // claim new instance
  const [instance, deployment] = await deploymentStore.createDeployment(
    useStarGz ? GENERIC_APP_ID : appID, // claim using generic ID if needed
    token,
  );
  if (!(instance && instance.fqdn)) {
    throw new Error('unable to acquire instance');
  }

  // race a call to the ready endpoint to speed up claim below
  // (starts tls handshake to fqdn as early as possible)
  wnextHTTP.get(`https://${instance.fqdn}/_leaf/ready`).catch(() => {});

  // ensure it is not already in the store by checking for fqdn
  dep = pollenStore.deployments.find((d) => d.fqdn === instance.fqdn);
  if (dep) {
    return redirectToDep(dep, isCode);
  }

  const accessKey = new WNextAccessAPI().createAccessKey({ fqdn: instance.fqdn });

  // create new deployment in pollen store
  dep = pollenStore.createDeployment(app.metadata, getHostByID(ComputeProviderName.WNext)!);
  dep.instanceName = deployment.name;
  dep.state = DeploymentState.Running;
  dep.fqdn = instance.fqdn;
  if (instance.configurekey) {
    dep.accessList.push(instance.configurekey);
  }
  // launch.deployment.appSettings = {}; // TODO: get from server

  const values: Values = {
    [CLOVYR_WNEXT_FQDN]: instance.fqdn,
  };
  addBackupValues(
    values,
    `${config.s3URL}/${dep.id}`,
    await accessKey,
    appConfig.VITE_PUBLIC_URL,
    instance.configurekey!,
  );
  dep.appSettings = values;

  if (isCode) {
    // claim the server for this client
    // only for clovyr code path
    await claimInstance({
      host: instance.fqdn,
      oldKey: instance.configurekey!,
      // FIXME: randomize new key and save it to pollen
      newKey: instance.configurekey!,
      resticConfig: resticConfigFromValues(values),
    });
  }

  await pollenStore.pollen.putDeployment(dep);
  await pollenStore.pollen.putInstance(instance);

  if (useStarGz) {
    // update image and wait for it to come back up
    console.time('runCode');
    await loading;
    await runCodeImageCompose(deploymentName, instance.fqdn, instance.configurekey!, app, values);
    console.timeEnd('runCode');
  }

  // show it now
  return redirectToDep(dep, isCode);
}
